/**
 * @fileoverview Settings → Plan & billing: what the account is on, what it has
 * left, and subscription management.
 *
 * Subscription and usage rows in one card, replacing repeated tiles
 * that each restated the plan in their own words:
 *
 *   1. **The plan.** Named from the EFFECTIVE tier — the server-resolved one
 *      that applies the grandfathering / founding pin and any org coverage —
 *      never from the stored `subscription_tier`. The stored field IS `basic`
 *      for a Founding Ensemble member, which is how this page came to tell
 *      exactly those accounts they were on Solo, with an Upgrade button,
 *      while the badge beside their name said Studio AI. The Stripe
 *      cancel/reactivate controls sit behind Manage rather than always open:
 *      cancelling is something somebody does once, not something to read
 *      past on every visit.
 *   2. **Recording minutes** — the X10 meter, with the top-up as a quiet
 *      trailing action. It is not a plan change: "I need more minutes" is a
 *      thought someone has while looking at how many they have left.
 *   3. **Students** — the roster against its cap, with the remedy named
 *      BEFORE the wall is hit (archiving keeps every lesson; deletion is what
 *      a cap with no way out pressures a teacher into).
 */

import { useState } from "react";
import { CheckCircle2, ExternalLink, Loader2 } from "lucide-react";

import RecordingUsage from "@/components/RecordingUsage";
import TopUpCheckout from "@/components/TopUpCheckout";
import { SettingsRow, SettingsSection } from "@/components/settings/SettingsSection";
import StripeManagePanel, { hasAppleSubscription, hasStripeSubscription, useStripeDetails } from "@/components/settings/StripeManagePanel";
import { Button } from "@/components/studio/primitives";
import { formatComplimentaryUntil, isFoundingMember } from "@/lib/founding";
import { topupLabel } from "@/lib/pricing";
import { TIER_ALLOWANCE_LABEL, TIER_TAGLINE, hasLessonAi, isPaidTier, maxActiveStudents, tierDisplayName } from "@/lib/tiers";
import { buildUsageModel } from "@/lib/usageMeter";

const ActivePill = () => (
  <span className="inline-flex items-center gap-1 font-sans text-[12px] font-medium text-[var(--state-success)]">
    <CheckCircle2 className="h-3 w-3" /> Active
  </span>
);

/**
 * Where an App Store plan is billed and when it renews or ends, in the words
 * the iPhone app uses (SettingsScreen's `billingNote`). Read from the
 * account's own `subscription_expires_at` and `subscription_auto_renews` on
 * /auth/me; an auto-renew state the store has not reported yet says "Paid
 * through" rather than guessing, and an unreadable date is shown as sent.
 */
function appStoreBillingNote(user) {
  const where = "Billed through the App Store.";
  const until = user?.subscription_expires_at;
  if (!until) return where;
  const when = new Date(until);
  const date = Number.isNaN(when.getTime())
    ? String(until)
    : when.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
  if (user.subscription_auto_renews === true) return `${where} Renews on ${date}.`;
  if (user.subscription_auto_renews === false) return `${where} Ends on ${date}.`;
  return `${where} Paid through ${date}.`;
}

/**
 * Row 1: the plan. See the file header for why `tier` is the effective one.
 */
function PlanRow({ user, tier, orgSeat, grandfathered, subsEnabled, canUpgrade, onUpgrade }) {
  const [manageOpen, setManageOpen] = useState(false);
  // The Stripe subscription belongs to the STORED tier: a pin grants
  // entitlement, it does not create a Stripe subscription to manage.
  const stripeSub = subsEnabled && hasStripeSubscription(user);
  const appleSub = hasAppleSubscription(user);
  const { details, loading, setDetails } = useStripeDetails(stripeSub);

  const founding = isFoundingMember(user);
  const until = formatComplimentaryUntil(user);
  const paid = isPaidTier(tier) || !!orgSeat;

  // One line under the plan name, chosen in priority order. Each says where
  // the entitlement comes from, because that is the instructor's actual
  // question — "why am I on this?" — not a restatement of the name above it.
  let desc;
  if (orgSeat) {
    desc = <span data-testid="seat-provided-by">Provided by {orgSeat.org_name}</span>;
  } else if (founding && until) {
    desc = (
      <span data-testid="founding-plan-note">
        Complimentary through {until} as a Founding Ensemble member. Your badge is permanent.
      </span>
    );
  } else if (grandfathered) {
    desc = (
      <span data-testid="grandfathered-notice">
        You keep {tierDisplayName(grandfathered.tier)} at your original price until{" "}
        {new Date(grandfathered.until).toLocaleDateString()}.
      </span>
    );
  } else if (stripeSub && details?.current_period_end) {
    desc = details.cancel_at_period_end
      ? `Ends ${new Date(details.current_period_end).toLocaleDateString()}`
      : `${details.plan ? `${details.plan.charAt(0).toUpperCase()}${details.plan.slice(1)} · ` : ""}renews ${new Date(details.current_period_end).toLocaleDateString()}`;
  } else if (appleSub) {
    desc = <span data-testid="app-store-billing">{appStoreBillingNote(user)}</span>;
  } else if (!isPaidTier(tier)) {
    desc = `Free — ${TIER_ALLOWANCE_LABEL.basic}, up to ${maxActiveStudents("basic")} active students`;
  } else {
    desc = TIER_TAGLINE[tier] || null;
  }

  let trailing = null;
  if (canUpgrade) {
    trailing = (
      <Button onPress={onUpgrade} className="px-5 py-2 text-[14px]" data-testid="upgrade-button">
        Upgrade
      </Button>
    );
  } else if (stripeSub) {
    trailing = (
      <Button
        onPress={() => setManageOpen((v) => !v)}
        variant="bare"
        iconRight={manageOpen ? "chevronUp" : "chevronDown"}
        className="px-4 py-2 text-[13px]"
        data-testid="manage-plan"
        aria-expanded={manageOpen}
      >
        Manage
      </Button>
    );
  } else if (appleSub) {
    trailing = (
      <a
        href="https://apps.apple.com/account/subscriptions"
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1 font-sans text-[13px] text-[var(--text-secondary)] transition-colors hover:text-[var(--text-primary)]"
      >
        Manage on iOS <ExternalLink className="h-3 w-3" />
      </a>
    );
  }

  return (
    <SettingsRow
      testId="subscription-card"
      title={
        <span className="flex flex-col gap-1">
          <span className="font-mono text-[11px] font-normal uppercase tracking-widest text-[var(--text-tertiary)]">
            Current subscription
          </span>
          <span className="inline-flex flex-wrap items-center gap-2">
            <span className="text-[17px]">{tierDisplayName(tier)}</span>
            {paid && <ActivePill />}
          </span>
        </span>
      }
      desc={desc}
      trailing={trailing}
    >
      {stripeSub && manageOpen && (
        <StripeManagePanel user={user} details={details} loading={loading} onDetails={setDetails} />
      )}
    </SettingsRow>
  );
}

/** Rows 2 + 3: the meter and the roster. Wrapped so one test id covers both. */
function UsageRows({ usage, loading, showTopUp, onCloseTopUp, onOpenTopUp, last }) {
  const contextCount = usage ? buildUsageModel(usage).contexts.length : 0;
  const atCap = Boolean(usage?.max_students) && usage?.student_count >= usage.max_students;

  // Offered only to a plan that records at all — `studio` is paid and
  // deliberately has no AI, so minutes would buy it nothing (I4/I6).
  const canTopUp = !loading && !!usage && hasLessonAi(usage.tier);

  return (
    <div data-testid="usage-card">
      <SettingsRow
        title="Recording minutes"
        // The price sits in the row's own line so the button can say the one
        // thing a button should — what pressing it does — and still leave the
        // figure on screen beside it.
        desc={canTopUp ? `Top up any time: ${topupLabel()}. Bought minutes never expire.` : null}
        align="start"
        trailing={
          canTopUp ? (
            showTopUp ? (
              // Pressed state while the checkout is open below: quieter and
              // smaller, so the form is what the eye lands on. Pressing it
              // again closes the checkout.
              <Button
                onPress={onCloseTopUp}
                variant="bare"
                className="px-3 py-1.5 text-[12px] text-[var(--accent-yellow-text)]"
                data-testid="add-minutes"
                aria-pressed="true"
              >
                Add minutes
              </Button>
            ) : (
              <Button onPress={onOpenTopUp} icon="plus" className="px-4 py-2 text-[14px]" data-testid="add-minutes">
                Add minutes
              </Button>
            )
          ) : null
        }
      >
        <div className="mt-3">
          {loading ? (
            <div className="flex items-center gap-2 font-sans text-sm text-[var(--text-tertiary)]">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading usage...
            </div>
          ) : !usage ? (
            <p className="font-sans text-sm text-[var(--text-tertiary)]">Unable to load usage data.</p>
          ) : showTopUp ? (
            <TopUpCheckout onClose={onCloseTopUp} />
          ) : (
            // One context needs no label of its own — the row is the label.
            // Several (an org member) keep theirs, or the bars are anonymous.
            <RecordingUsage usage={usage} labels={contextCount > 1} />
          )}
        </div>
      </SettingsRow>

      {!loading && usage && (
        <SettingsRow
          title="Students"
          // Named here rather than left for the 403, because the wall is far
          // kinder met before you have typed a student's name in than after.
          desc={
            atCap
              ? "Your roster is full. Archive a student you've finished teaching — every lesson is kept — or upgrade for an unlimited roster."
              : null
          }
          trailing={
            // `max_students` is null on every paid plan and 5 on free (I1),
            // read from the payload because the server resolves it org-aware.
            // No progress bar, on purpose: a bar reads as a cycle you are
            // burning through, and a roster is neither spent nor refilled.
            <span className="inline-flex items-baseline gap-1 font-mono text-[13px]">
              <span className="text-[var(--text-secondary)]">{usage.student_count} active</span>
              <span className="text-[var(--text-tertiary)]">{usage.max_students ? `of ${usage.max_students}` : "Unlimited"}</span>
            </span>
          }
          last={last}
        />
      )}
    </div>
  );
}

/**
 * @param {object} props
 * @param {object} props.user The instructor from /auth/me.
 * @param {string} props.tier The EFFECTIVE tier (server-resolved).
 * @param {object|null} props.orgSeat The covering org seat, if any.
 * @param {object|null} props.grandfathered X7's `{tier, until}` pin, if live.
 * @param {boolean} props.subsEnabled The server-resolved paywall flag.
 * @param {object|null} props.usage The `/subscription/usage` payload.
 * @param {boolean} props.usageLoading
 * @param {boolean} props.showTopUp Whether the top-up checkout replaces the meter.
 * @param {function} props.onOpenTopUp
 * @param {function} props.onCloseTopUp
 * @param {boolean} props.canUpgrade Whether the Upgrade control is offered.
 * @param {function} props.onUpgrade Opens the plan dialog.
 */
export default function PlanSection({
  user,
  tier,
  orgSeat,
  grandfathered,
  subsEnabled,
  usage,
  usageLoading,
  showTopUp,
  onOpenTopUp,
  onCloseTopUp,
  canUpgrade,
  onUpgrade,
}) {
  if (!subsEnabled) return null;

  return (
    <SettingsSection title="Plan & billing" testId="plan-section">
      {subsEnabled && (
        <>
          <PlanRow
            user={user}
            tier={tier}
            orgSeat={orgSeat}
            grandfathered={grandfathered}
            subsEnabled={subsEnabled}
            canUpgrade={canUpgrade}
            onUpgrade={onUpgrade}
          />
          <UsageRows
            usage={usage}
            loading={usageLoading}
            showTopUp={showTopUp}
            onOpenTopUp={onOpenTopUp}
            onCloseTopUp={onCloseTopUp}
            last
          />
        </>
      )}
    </SettingsSection>
  );
}
